// Design tokens for the homepage ("Homepage B", white ground).
// Source of truth: Canyou brand guide (Claude Design, Sep 2026) plus the
// approved homepage canvas. Reuse these for any page built in this style.

/**
 * Theme values. Neutral colours are CSS variables so the site can switch
 * between white, cream and dark (see ThemeToggle). Brand colours are fixed.
 */
export const themes = {
  white: {
    page: '#FFFFFF',
    surface: '#FFFFFF',
    paper: '#F6F7F9',
    ink: '#0E1220',
    inkBg: '#0E1220',
    ink70: '#4A5261',
    muted: '#5F6778', // 5.3:1 on the grey band (was #6B7383, 4.45:1)
    line: '#E3E6EC',
    zebra: '#FAFBFD',
    nav: 'rgba(255,255,255,.94)',
  },
  cream: {
    page: '#FFFCEB',
    surface: '#FFFFFF',
    paper: '#F7F0D8',
    ink: '#0E1220',
    inkBg: '#0E1220',
    ink70: '#4A5261',
    muted: '#5F6778', // 5.3:1 on the grey band (was #6B7383, 4.45:1)
    line: '#E9E2C8',
    zebra: '#FFFDF4',
    nav: 'rgba(255,252,235,.94)',
  },
  dark: {
    page: '#0B0F1A',
    surface: '#FFFFFF', // cards stay white on the dark page (reversed panels)
    paper: '#10162A',
    ink: '#E9EDF5',
    inkBg: '#1C2438',
    ink70: '#B4BAC5',
    muted: '#8E96A6',
    line: '#2B3348',
    zebra: '#182033',
    nav: 'rgba(11,15,26,.92)',
  },
} as const;

export type ThemeName = keyof typeof themes;

const v = (name: keyof (typeof themes)['white']) =>
  `var(--cy-${name}, ${themes.white[name]})`;

export const colors = {
  blue: '#2667FF', // Canyou Blue: actions, links, emphasis
  press: '#0F47D6', // hover / pressed blue
  ink: v('ink'), // text, outlines, offset shadows
  inkBg: v('inkBg'), // dark bands and buttons (stays dark in dark mode)
  ink70: v('ink70'), // body copy on light grounds
  muted: v('muted'), // captions
  paper: v('paper'), // alternating bands
  line: v('line'), // hairlines
  white: v('surface'), // cards and light surfaces
  page: v('page'), // page background
  zebra: v('zebra'), // alternate table rows
  navBg: v('nav'), // sticky nav background
  onDark: '#FFFFFF', // text and icons on blue or dark bands (never changes)
  onLight: '#0E1220', // text on bright accent fills like lime (never changes)
  verified: '#12A66A', // compliant / synced status
  verifiedText: '#0E7A4F',
  verifiedWash: '#E6F7EF',
  lime: '#A3E635', // highlight pills, dark-section links
  // Offset-shadow accents (decoration only, never for status)
  sky: '#38BDF8',
  mint: '#86EFAC',
  rose: '#FCA5A5',
  lilac: '#D8B4FE',
  orange: '#FB923C',
  yellow: '#FDE047',
  // Dark section
  darkCard: '#1A1F2E',
  darkLine: '#2B3142',
  darkText: '#B4BAC5',
  footerText: '#9AA1AE',
} as const;

export const fonts = {
  // Canyou Dollar contains only U+0024, so all other display characters fall
  // through to Playfair while prices keep the familiar single-stroke glyph.
  display: "'Canyou Dollar', 'Playfair Display', Georgia, serif",
  body: "'Figtree', system-ui, sans-serif",
} as const;

export const ease = 'cubic-bezier(.22,1,.36,1)';

/** Outlined card with a hard offset shadow: the signature component. */
export const outlinedCard = (shadow?: string, offset = 8) => ({
  bg: colors.white,
  className: 'cy-surface',
  border: '2px solid',
  borderColor: colors.ink,
  borderRadius: '16px',
  boxShadow: shadow ? `${offset}px ${offset}px 0 ${shadow}` : undefined,
});
